import {
  Component,
  inject,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef,
  NgZone,
} from '@angular/core';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  EXPORT_QUALITY,
  exportMimeType,
  exportScale,
  renderCrop,
} from '../../../utils/image-export.util';

interface AspectRatio {
  label: string;
  value: string;
  ratio: number;
}

@Component({
  selector: 'app-image-croping',
  standalone: true,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    CommonModule,
    FormsModule,
    MatTooltipModule,
  ],
  templateUrl: './image-croping.component.html',
  styleUrl: './image-croping.component.css',
})
export class ImageCropingComponent implements OnInit, AfterViewInit {
  @ViewChild('canvas', { static: false }) canvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('imageElement', { static: false }) imageRef!: ElementRef<HTMLImageElement>;
  @ViewChild('cropContainer', { static: false }) cropContainerRef!: ElementRef<HTMLDivElement>;

  readonly dialogRef = inject(MatDialogRef<ImageCropingComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  private readonly zone = inject(NgZone);

  imageSrc: string = '';
  aspectRatios: AspectRatio[] = [
    // { label: 'Original', value: 'original', ratio: 0 },
    // { label: 'Square (1:1)', value: '1:1', ratio: 1 },
    // { label: 'Wide (16:9)', value: '16:9', ratio: 16 / 9 },
    // { label: 'Standard (4:3)', value: '4:3', ratio: 4 / 3 },
    // { label: 'Logo (159:86)', value: '159:86', ratio: 159 / 86 }
  ];

  selectedAspectRatio: string = '159:86';
  logoSize: number = 100;
  minSize: number = 50;
  maxSize: number = 150;
  targetWidth: number = 159;
  targetHeight: number = 86;
  title: string = 'Crop Image';

  private image: HTMLImageElement | null = null;
  private cropX: number = 0;
  private cropY: number = 0;
  private cropWidth: number = 0;
  private cropHeight: number = 0;
  private imageScale: number = 1;
  private imageX: number = 0;
  private imageY: number = 0;
  private imageDisplayWidth: number = 0;
  private imageDisplayHeight: number = 0;
  private containerWidth: number = 500;
  private containerHeight: number = 400;
  private isDragging: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private initialCropX: number = 0;
  private initialCropY: number = 0;
  private imageLoaded: boolean = false;

  // Corner resize: the opposite corner stays put and the ratio stays locked.
  private readonly handleSize = 10;
  private readonly handleHitArea = 14;
  private resizeAnchor: { x: number; y: number } | null = null;
  cursor = 'default';

  ngOnInit() {
    if (this.data && this.data.imageSrc) {
      this.imageSrc = this.data.imageSrc;
      this.loadImage();
    }
    // Allow custom target dimensions from data
    if (this.data && this.data.targetWidth) {
      this.targetWidth = this.data.targetWidth;
    }
    if (this.data && this.data.targetHeight) {
      this.targetHeight = this.data.targetHeight;
    }
    // Allow custom aspect ratio selection from data
    if (this.data && this.data.defaultAspectRatio) {
      this.selectedAspectRatio = this.data.defaultAspectRatio;
    }
    if (this.data && this.data.title) {
      this.title = this.data.title;
    }
    this.aspectRatios.push({
      label: 'Standard',
      value: this.targetWidth + ':' + this.targetHeight,
      ratio: this.targetWidth / this.targetHeight,
    });
    // The crop ratio is always the caller's target ratio; users cannot change it.
    this.selectedAspectRatio = this.targetWidth + ':' + this.targetHeight;
    this.updateCropDimensions();
  }

  ngAfterViewInit() {
    // Wait for view to initialize before calculating dimensions
    setTimeout(() => {
      if (this.imageLoaded) {
        this.initializeCrop();
      }
    }, 200);
  }

  loadImage() {
    this.image = new Image();
    this.image.crossOrigin = 'anonymous';
    this.image.onload = () => {
      this.imageLoaded = true;
      setTimeout(() => {
        this.initializeCrop();
      }, 100);
    };
    this.image.onerror = () => {
      console.error('Failed to load image');
    };
    this.image.src = this.imageSrc;
  }

  initializeCrop() {
    if (!this.image) return;

    const container = this.cropContainerRef?.nativeElement;
    if (!container) {
      setTimeout(() => this.initializeCrop(), 50);
      return;
    }

    this.containerWidth = container.offsetWidth || 500;
    this.containerHeight = container.offsetHeight || 400;

    const imageAspect = this.image.width / this.image.height;
    const containerAspect = this.containerWidth / this.containerHeight;

    // Calculate display size to fit container
    if (imageAspect > containerAspect) {
      this.imageDisplayWidth = this.containerWidth * 0.9;
      this.imageDisplayHeight = this.imageDisplayWidth / imageAspect;
    } else {
      this.imageDisplayHeight = this.containerHeight * 0.9;
      this.imageDisplayWidth = this.imageDisplayHeight * imageAspect;
    }

    this.imageScale = this.imageDisplayWidth / this.image.width;
    this.imageX = (this.containerWidth - this.imageDisplayWidth) / 2;
    this.imageY = (this.containerHeight - this.imageDisplayHeight) / 2;

    // Initialize crop area
    const aspect = this.getAspectRatio();
    const cropSize = Math.min(this.imageDisplayWidth, this.imageDisplayHeight) * 0.7;

    if (aspect > 1) {
      this.cropWidth = cropSize * aspect;
      this.cropHeight = cropSize;
    } else {
      this.cropWidth = cropSize;
      this.cropHeight = cropSize / aspect;
    }

    // Center crop area
    this.cropX = (this.containerWidth - this.cropWidth) / 2;
    this.cropY = (this.containerHeight - this.cropHeight) / 2;

    this.constrainCropArea();
    this.updateCanvas();
  }

  updateCropDimensions() {
    if (!this.image) return;

    const aspect = this.getAspectRatio();
    const centerX = this.cropX + this.cropWidth / 2;
    const centerY = this.cropY + this.cropHeight / 2;

    // Calculate new crop size based on aspect ratio
    let newWidth: number;
    let newHeight: number;

    if (this.selectedAspectRatio === 'original') {
      // Use image's original aspect ratio
      const imageAspect = this.image.width / this.image.height;
      const currentArea = this.cropWidth * this.cropHeight;
      newHeight = Math.sqrt(currentArea / imageAspect);
      newWidth = newHeight * imageAspect;
    } else {
      // Use selected aspect ratio
      const currentSize = Math.min(this.cropWidth, this.cropHeight);
      if (aspect > 1) {
        // Landscape
        newWidth = currentSize * aspect;
        newHeight = currentSize;
      } else {
        // Portrait or square
        newWidth = currentSize;
        newHeight = currentSize / aspect;
      }
    }

    this.cropWidth = newWidth;
    this.cropHeight = newHeight;

    // Keep center position
    this.cropX = centerX - this.cropWidth / 2;
    this.cropY = centerY - this.cropHeight / 2;

    // Keep crop area within bounds
    this.constrainCropArea();
    this.updateCanvas();
  }

  getAspectRatio(): number {
    const selected = this.aspectRatios.find((r) => r.value === this.selectedAspectRatio);
    if (!selected || selected.ratio === 0 || this.selectedAspectRatio === 'original') {
      // Return original image aspect ratio
      return this.image ? this.image.width / this.image.height : 1;
    }
    if (this.selectedAspectRatio === '159:86') {
      return this.targetWidth / this.targetHeight;
    }
    return selected.ratio;
  }

  onSizeChange() {
    if (!this.image) return;

    const scale = this.logoSize / 100;
    const baseSize = Math.min(this.imageDisplayWidth, this.imageDisplayHeight) * 0.7;
    const newSize = baseSize * scale;

    const aspect = this.getAspectRatio();
    const centerX = this.cropX + this.cropWidth / 2;
    const centerY = this.cropY + this.cropHeight / 2;

    if (aspect > 1) {
      this.cropWidth = newSize * aspect;
      this.cropHeight = newSize;
    } else {
      this.cropWidth = newSize;
      this.cropHeight = newSize / aspect;
    }

    // Keep center position
    this.cropX = centerX - this.cropWidth / 2;
    this.cropY = centerY - this.cropHeight / 2;

    this.constrainCropArea();
    this.updateCanvas();
  }

  constrainCropArea() {
    const container = this.cropContainerRef?.nativeElement;
    if (!container) return;

    const maxX = container.offsetWidth - this.cropWidth;
    const maxY = container.offsetHeight - this.cropHeight;

    this.cropX = Math.max(0, Math.min(this.cropX, maxX));
    this.cropY = Math.max(0, Math.min(this.cropY, maxY));
  }

  onMouseDown(event: MouseEvent) {
    const rect = this.cropContainerRef?.nativeElement.getBoundingClientRect();
    if (!rect) return;

    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const corner = this.getCornerAt(x, y);
    if (corner) {
      // Resize from this corner; the diagonally opposite corner is the anchor.
      this.resizeAnchor = {
        x: corner.isLeft ? this.cropX + this.cropWidth : this.cropX,
        y: corner.isTop ? this.cropY + this.cropHeight : this.cropY,
      };
      event.preventDefault();
      return;
    }

    if (this.isPointInCropArea(x, y)) {
      this.isDragging = true;
      this.dragStartX = x;
      this.dragStartY = y;
      this.initialCropX = this.cropX;
      this.initialCropY = this.cropY;
    }
  }

  onMouseMove(event: MouseEvent) {
    const rect = this.cropContainerRef?.nativeElement.getBoundingClientRect();
    if (!rect) return;
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    if (this.resizeAnchor) {
      this.resizeFromCorner(x, y, rect.width, rect.height);
      return;
    }

    if (!this.isDragging) {
      this.cursor = this.cursorAt(x, y);
      return;
    }

    const deltaX = x - this.dragStartX;
    const deltaY = y - this.dragStartY;

    this.cropX = this.initialCropX + deltaX;
    this.cropY = this.initialCropY + deltaY;

    this.constrainCropArea();
    this.updateCanvas();
  }

  onMouseUp() {
    this.isDragging = false;
    this.resizeAnchor = null;
  }

  /** Which corner handle (if any) is under the pointer. */
  private getCornerAt(x: number, y: number): { isLeft: boolean; isTop: boolean } | null {
    const hit = this.handleHitArea;
    const nearLeft = Math.abs(x - this.cropX) <= hit;
    const nearRight = Math.abs(x - (this.cropX + this.cropWidth)) <= hit;
    const nearTop = Math.abs(y - this.cropY) <= hit;
    const nearBottom = Math.abs(y - (this.cropY + this.cropHeight)) <= hit;
    if ((nearLeft || nearRight) && (nearTop || nearBottom)) {
      return { isLeft: nearLeft, isTop: nearTop };
    }
    return null;
  }

  private cursorAt(x: number, y: number): string {
    const corner = this.getCornerAt(x, y);
    if (corner) {
      return corner.isLeft === corner.isTop ? 'nwse-resize' : 'nesw-resize';
    }
    return this.isPointInCropArea(x, y) ? 'move' : 'default';
  }

  /**
   * Resize keeping the aspect ratio: the box grows or shrinks from the anchor
   * (the opposite corner) toward the pointer, staying inside the container and
   * within the size slider's range, which is kept in sync.
   */
  private resizeFromCorner(x: number, y: number, containerW: number, containerH: number) {
    const anchor = this.resizeAnchor!;
    const aspect = this.getAspectRatio();
    const growLeft = x < anchor.x;
    const growUp = y < anchor.y;

    // Follow whichever axis the pointer has moved further on, at the locked ratio.
    let width = Math.max(Math.abs(x - anchor.x), Math.abs(y - anchor.y) * aspect);

    const roomX = growLeft ? anchor.x : containerW - anchor.x;
    const roomY = growUp ? anchor.y : containerH - anchor.y;
    const baseSize = Math.min(this.imageDisplayWidth, this.imageDisplayHeight) * 0.7;
    const sizeToWidth = (size: number) => (aspect > 1 ? size * aspect : size);
    const minWidth = sizeToWidth((baseSize * this.minSize) / 100);
    const maxWidth = Math.min(roomX, roomY * aspect, sizeToWidth((baseSize * this.maxSize) / 100));
    width = Math.max(Math.min(width, maxWidth), Math.min(minWidth, maxWidth));

    this.cropWidth = width;
    this.cropHeight = width / aspect;
    this.cropX = growLeft ? anchor.x - this.cropWidth : anchor.x;
    this.cropY = growUp ? anchor.y - this.cropHeight : anchor.y;

    const size = aspect > 1 ? this.cropHeight : this.cropWidth;
    this.logoSize = Math.round((size / baseSize) * 100);
    this.updateCanvas();
  }

  isPointInCropArea(x: number, y: number): boolean {
    return (
      x >= this.cropX &&
      x <= this.cropX + this.cropWidth &&
      y >= this.cropY &&
      y <= this.cropY + this.cropHeight
    );
  }

  updateCanvas() {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas || !this.image) return;

    const container = this.cropContainerRef?.nativeElement;
    if (!container) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = container.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.scale(dpr, dpr);
    canvas.style.width = rect.width + 'px';
    canvas.style.height = rect.height + 'px';

    // Clear canvas
    ctx.clearRect(0, 0, rect.width, rect.height);

    // Draw the full image first
    ctx.drawImage(
      this.image,
      this.imageX,
      this.imageY,
      this.imageDisplayWidth,
      this.imageDisplayHeight,
    );

    // Save the current state
    ctx.save();

    // Create a clipping path for the overlay (everything except crop area)
    ctx.beginPath();
    ctx.rect(0, 0, rect.width, rect.height);
    ctx.rect(this.cropX + this.cropWidth, this.cropY, -this.cropWidth, this.cropHeight);
    ctx.clip('evenodd');

    // Draw overlay on non-crop areas
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, rect.width, rect.height);

    // Restore the state
    ctx.restore();

    // Redraw the image in the crop area to ensure original colors
    const sourceX = (this.cropX - this.imageX) / this.imageScale;
    const sourceY = (this.cropY - this.imageY) / this.imageScale;
    const sourceWidth = this.cropWidth / this.imageScale;
    const sourceHeight = this.cropHeight / this.imageScale;

    ctx.drawImage(
      this.image,
      Math.max(0, sourceX),
      Math.max(0, sourceY),
      Math.min(sourceWidth, this.image.width - Math.max(0, sourceX)),
      Math.min(sourceHeight, this.image.height - Math.max(0, sourceY)),
      this.cropX,
      this.cropY,
      this.cropWidth,
      this.cropHeight,
    );

    // Draw crop border
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.setLineDash([]);
    ctx.strokeRect(this.cropX, this.cropY, this.cropWidth, this.cropHeight);

    // Draw corner handles (draggable to resize, see resizeFromCorner)
    const handleSize = this.handleSize;
    ctx.fillStyle = '#ffffff';
    const corners = [
      [this.cropX, this.cropY],
      [this.cropX + this.cropWidth, this.cropY],
      [this.cropX, this.cropY + this.cropHeight],
      [this.cropX + this.cropWidth, this.cropY + this.cropHeight],
    ];
    corners.forEach(([x, y]) => {
      ctx.fillRect(x - handleSize / 2, y - handleSize / 2, handleSize, handleSize);
    });
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSave() {
    if (!this.image) return;

    // Calculate source crop coordinates relative to original image
    const sourceX = Math.max(0, (this.cropX - this.imageX) / this.imageScale);
    const sourceY = Math.max(0, (this.cropY - this.imageY) / this.imageScale);
    const sourceWidth = Math.min(this.cropWidth / this.imageScale, this.image.width - sourceX);
    const sourceHeight = Math.min(this.cropHeight / this.imageScale, this.image.height - sourceY);

    // Ensure we don't go outside image bounds
    const finalWidth = Math.min(sourceWidth, this.image.width - sourceX);
    const finalHeight = Math.min(sourceHeight, this.image.height - sourceY);

    // Export the master (up to 1600px, or data.maxLongEdge); the CDN serves smaller sizes
    const scale = exportScale(
      finalWidth,
      this.targetWidth,
      this.targetHeight,
      this.data?.maxLongEdge,
    );
    const width = Math.round(this.targetWidth * scale);
    const height = Math.round(this.targetHeight * scale);
    const mimeType = exportMimeType(!!this.data?.allowTransparency);

    const canvas = renderCrop(
      this.image,
      sourceX,
      sourceY,
      finalWidth,
      finalHeight,
      width,
      height,
      mimeType === 'image/jpeg',
    );

    // Convert to blob
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const reader = new FileReader();
          reader.onloadend = () => {
            // zone.js does not patch canvas.toBlob, so this callback runs outside
            // Angular's zone. Close inside it, or the caller's upload and form
            // update never trigger change detection (Save stayed disabled).
            this.zone.run(() =>
              this.dialogRef.close({
                croppedImage: reader.result as string,
                blob: blob,
                mimeType: blob.type,
                width,
                height,
              }),
            );
          };
          reader.readAsDataURL(blob);
        } else {
          console.error('Failed to create blob from canvas');
        }
      },
      mimeType,
      EXPORT_QUALITY,
    );
  }
}
